<template>
  <div class="ak-drag-and-drop-tree">
    <ul class="pl-none my-none">
      <template v-for="(child, index) in treeData" :key="index">
        <ak-drag-and-drop-tree-item
          v-model:children="child.children"
          :data="child.data"
          :parent="{ data: null, children: data }"
          :index="index"
          @update-children="updateChildren"
        >
          <template #default="slotProps">
            <slot
              :item="slotProps.item"
              :open="slotProps.open"
              :is-tree="slotProps.isTree"
              :children="slotProps.children"
            />
          </template>
          <template #empty>
            <slot name="empty">Empty</slot>
          </template>
        </ak-drag-and-drop-tree-item>
      </template>
    </ul>
  </div>
</template>

<script>
import { defineComponent, ref, watch, provide } from 'vue'
import DragAndDropTreeItem from './DragAndDropTreeItem.vue'
export default defineComponent({
  name: 'AkDragAndDropTree',
  components: {
    [DragAndDropTreeItem.name]: DragAndDropTreeItem,
  },
  props: {
    /**
     * @type {Array}
     * @description The data to model for the tree
     */
    data: {
      type: Array,
      default: () => [],
    },
    /**
     * @type {Boolean}
     * @description Should the tree items that have sub trees show empty locations
     */
    showEmpty: {
      type: Boolean,
      default: false,
    },
    /**
     * @type {Number}
     * @description The max depth trees can go to
     */
    maxTreeDepth: {
      type: Number,
      default: -1,
    },
    /**
     * @type {Number}
     * @description the max depth tree items can go to
     */
    maxItemDepth: {
      type: Number,
      default: -1,
    },
    /**
     * @type {Function}
     * @description A function to determine if an item is a sub tree of just an item
     */
    isTree: {
      type: Function,
      default: () => {
        return true
      },
    },
    isOpen: {
      type: Function,
      default: () => {
        return false
      },
    },
    extraItemClass: {
      type: Function,
      default: () => {
        return {}
      },
    },
  },
  emits: ['move', 'update:data', 'treeOpened', 'treeClosed'],
  setup(props, { emit }) {
    const treeData = ref(props.data)
    const dragTarget = ref(null)
    const dragHTML = ref('')
    const dropTarget = ref(null)
    const dropIndex = ref(null)
    const removeDragItem = ref(null)
    const isDragTargetTree = ref(false)
    const dragTargetDepth = ref(0)

    /**
     * @description Update the internal data if the prop changes
     */
    watch(
      () => props.data,
      (value) => {
        treeData.value = value
      },
      { deep: true }
    )

    /**
     * @param {Object} item
     * @param {String} html
     * @param {Function} func
     * @description Set the data needed for dragging an item
     */
    const setDragTarget = (item, html, func = null) => {
      dragTarget.value = item
      dragHTML.value = html
      removeDragItem.value = func
      isDragTargetTree.value = props.isTree(item.data)
      dragTargetDepth.value = calcTreeDepth(item)
    }

    /**
     * @param {Object} item
     * @param {Number} index
     * @description Set the data for where a dragged item could be dropped
     */
    const setDropTarget = (item, index = null) => {
      dropTarget.value = item
      dropIndex.value = index
    }

    /**
     * @description Emits when an item is dropped onto a location and reset the data
     */
    const moveItem = (dropItem = null) => {
      const item = { ...dragTarget.value }
      const target = Array.isArray(dropTarget.value)
        ? 'root'
        : { ...dropTarget.value }
      const targetIndex = dropIndex.value
      emit('move', { item, target, targetIndex, dropItem })
      dragTarget.value = null
      dragHTML.value = ''
      dropTarget.value = null
      dropIndex.value = null
      removeDragItem.value = null
      isDragTargetTree.value = false
      dragTargetDepth.value = 0
    }

    /**
     * @description Get the current drag targets html
     * @return {String}
     */
    const getDragHTML = () => {
      return dragHTML.value
    }

    /**
     * @description Get the currently dragged item
     * @return {Object}
     */
    const getDragTarget = () => {
      return dragTarget.value
    }

    /**
     * @description Is the current dragged item a tree or not
     * @return {Boolean}
     */
    const getIsDragTargetTree = () => {
      return isDragTargetTree.value
    }

    /**
     * @description Get the depth of a tree item and its children
     * @return {Number}
     */
    const getDragTargetDepth = () => {
      return dragTargetDepth.value
    }

    /**
     * @param {Array} array
     * @description Update the children of the item
     */
    const updateChildren = (array) => {
      emit('update:data', array)
    }

    /**
     * @description remove the tree item from it's original data location
     */
    const updateOriginalLocation = () => {
      if (typeof removeDragItem.value === 'function') {
        removeDragItem.value()
      }
    }

    /**
     * @param {Object} target
     * @description Calculate the total depth of an item and it's children
     */
    const calcTreeDepth = (target) => {
      const tree = props.isTree(target.data)
      if (!tree || !target.children) return 1
      let childDepth = 0
      for (let i = 0; i < target.children.length; i++) {
        const child = target.children[i]
        if (child) {
          childDepth = Math.max(childDepth, calcTreeDepth(child))
        }
      }
      return childDepth
    }

    /**
     * @description Provide functions to tree items so we don't have to send in loads of props
     */
    provide('showEmpty', props.showEmpty)
    provide('extraItemClass', props.extraItemClass)
    provide('setDragTarget', setDragTarget)
    provide('setDropTarget', setDropTarget)
    provide('moveItem', moveItem)
    provide('getDragHTML', getDragHTML)
    provide('getDragTarget', getDragTarget)
    provide('updateOriginalLocation', updateOriginalLocation)
    provide('getIsDragTargetTree', getIsDragTargetTree)
    provide('getDragTargetDepth', getDragTargetDepth)
    provide('isTree', (item) => {
      return props.isTree(item)
    })
    provide('maxDepth', () => {
      return props.maxTreeDepth
    })
    provide('maxItemDepth', () => {
      return props.maxItemDepth
    })
    provide('treeOpened', (item) => {
      emit('treeOpened', item)
    })
    provide('treeClosed', (item) => {
      emit('treeClosed', item)
    })
    provide('isOpen', props.isOpen)
    return {
      treeData,
      updateChildren,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-drag-and-drop-tree {
  font-size: $font-size-small;
  ul {
    padding-left: $spacing-tiny;
    list-style: none;
  }
}
</style>
